import { Component, OnInit, Output, EventEmitter, ViewChild, Input } from '@angular/core';
import { CampaignService } from '../../../../services/campaign/campaign.service';
import { FormGroup, Validators, FormControl } from '@angular/forms';
import { WebUtils } from '../../../../utils/web.utils';
import { UserService } from '../../../../services/user.service';
import { UserDetails, User } from '../../../../../models/user';
import { AchooPopupComponent } from '../../../../core/achoo-popup/achoo-popup.component';
import { TranslateService } from '@ngx-translate/core';
import { ModalService } from '../../../../services/modal/modal.service';
import { EventTrackerService, UserActionType, Page, TargetType } from '../../../../services/event-tracker/event-tracker.service';
import { CommonConstant } from '../../../../app.constants';
import { ObjectUtils } from '../../../../utils/object.utils';
import * as Sentry from '@sentry/browser';

@Component({
  selector: 'user-details',
  templateUrl: './user-details.component.html',
  styleUrls: ['./user-details.component.scss']
})
export class UserDetailsComponent implements OnInit {

  @ViewChild('UserDetailNotificationPopup')
  public userDetailNotificationPopup: AchooPopupComponent;
  private _userDetailNotificationPopupRef;
  @ViewChild('ImageInput') imageInput;

  @Input() currentUserDetails: UserDetails = new UserDetails();
  @Input() isUpdatePasswordSuccess: boolean;

  @Output() submitUserDetails: EventEmitter<any> = new EventEmitter();


  public previousUserDetails: UserDetails;
  public userDetails: FormGroup;
  public uploadedImage;
  public msgString: string;
  public isNotification: boolean;
  public popupTitle: string;
  public userData: User;
  public selectedImage;
  public hasChangedImage = false;
  private isUploadImgSuccess = false;
  private previousUserAvatar: string;

  constructor(private campaignService: CampaignService,
              private userService: UserService,
              private modalService: ModalService,
              private translate: TranslateService,
              private eventTracker: EventTrackerService) { }

  validateEmail(emailFormControl) {
    if (!emailFormControl) {
      return null;
    }
    if (!WebUtils.validateEmail(emailFormControl.value)) {
      return {
        invalidEmail: true
      };
    }
    return null;
  }

  ngOnInit() {
    this.userDetails = new FormGroup({
      'firstName': new FormControl(this.currentUserDetails.firstName, [Validators.required]),
      'lastName': new FormControl(this.currentUserDetails.lastName, [Validators.required]),
      'email': new FormControl(this.currentUserDetails.email, [Validators.required, this.validateEmail.bind(this)]),
      'phoneNumber': new FormControl(this.currentUserDetails.phone, [Validators.required])
    });
    this.getProfileImage();
  }

  get firstName() {
    return this.userDetails.get('firstName');
  }

  get lastName() {
    return this.userDetails.get('lastName');
  }

  get email() {
    return this.userDetails.get('email');
  }

  get phoneNumber() {
    return this.userDetails.get('phoneNumber');
  }

  onSubmitUserDetails() {
    if (!this.userDetails.valid) {
      return;
    }
    this.saveUserDetailsData();
  }

  private saveUserDetailsData() {
    if (!this.userData) {
      return;
    }
    // prepare data for sending to ws
    const userProfileData = {
      firstName: this.userDetails.value.firstName,
      lastName: this.userDetails.value.lastName,
      email: this.userDetails.value.email,
      phone: this.userDetails.value.phoneNumber,
      image: this.currentUserDetails.image
    };
    this.userService.updateProfile(userProfileData).subscribe((res) => {
      this.handleNotificationTitle(true);
      this.msgString = this.translate.instant('account_settings.notification_msg.update_user_details_success');
      this.modalService.closeSinglePopup(this._userDetailNotificationPopupRef);
      this._userDetailNotificationPopupRef = this.modalService.openMultipleModal(this.userDetailNotificationPopup, null);
      this.sendMixpanelEvents(false);
    }, (err) => {
      // rollback the user avatar to the previous image
      this.userService.updateCurrentUserAvatar(this.previousUserAvatar, this.userData);
      this.uploadedImage = this.userData.data && this.userData.data.image;
      this.handleNotificationTitle(false);
      if (this.isUpdatePasswordSuccess && this.isUploadImgSuccess) {
        this.msgString = this.translate.instant('account_settings.notification_msg.update_pwd_and_img_success_but_details_failed');
      } else {
        this.msgString = this.translate.instant('account_settings.notification_msg.update_failed');
      }this.modalService.closeSinglePopup(this._userDetailNotificationPopupRef);
      this._userDetailNotificationPopupRef = this.modalService.openMultipleModal(this.userDetailNotificationPopup, null);
      Sentry.captureException(new Error('Failed to update user profile'));
    });
  }

  onProfilePictureSelected(evt) {
    if (!evt || !evt.target || !evt.target.files) {
      return;
    }
    this.hasChangedImage = true;
    const selectedImageList = <FileList>((<any>evt.target).files);
    if (!selectedImageList || selectedImageList.length === 0) {
      return;
    }
    this.selectedImage = selectedImageList[0];
    if (this.selectedImage.size / 1024 > CommonConstant.MAX_PROFILE_PICTURE_SIZE) {
      this.handleNotificationTitle(false);
      this.msgString = this.translate.instant('account_settings.notification_msg.large_img');
      this.modalService.closeSinglePopup(this._userDetailNotificationPopupRef);
      this._userDetailNotificationPopupRef = this.modalService.open(this.userDetailNotificationPopup, null, true);
      return;
    }
    const file: File = this.selectedImage;
    const fileReader: FileReader = new FileReader();

    fileReader.onloadend = (e) => {
      this.uploadedImage = fileReader.result;
    };
    fileReader.readAsDataURL(file);
  }

  uploadProfilePicture(image) {
    // Only upload one file on each select
    const formData = new FormData();
    formData.append('file', image);
    this.imageInput.nativeElement.value = '';
    this.previousUserAvatar = this.currentUserDetails.image;
    this.campaignService.uploadFile(formData).subscribe((data) => {
      if (data) {
        this.currentUserDetails.image = data.diskFileName;
        this.isUploadImgSuccess = true;
        // Immediately show the new user avatar in UI
        this.userService.updateCurrentUserAvatar(this.currentUserDetails.image, this.userData);
        this.onSubmitUserDetails();
        this.sendMixpanelEvents(true);
      }
    }, (error) => {
      this.hasChangedImage = false;
      this.isUploadImgSuccess = false;
      // rollback the user avatar to the previous image
      this.userService.updateCurrentUserAvatar(this.previousUserAvatar, this.userData);
      this.uploadedImage = this.userData.data && this.userData.data.image || this.currentUserDetails.image;
      this.handleNotificationTitle(false);
      if (this.isUpdatePasswordSuccess) {
        this.msgString = this.translate.instant('account_settings.notification_msg.update_pwd_success_and_img_failed');
      } else {
        this.msgString = this.translate.instant('account_settings.notification_msg.upload_image_fail');
      }
      this.modalService.closeSinglePopup(this._userDetailNotificationPopupRef);
      this._userDetailNotificationPopupRef = this.modalService.open(this.userDetailNotificationPopup, null, true);
      Sentry.captureException(new Error('Upload failed on Settings page'));
    });
  }

  getProfileImage() {
    this.userData = ObjectUtils.parseJSON(localStorage.getItem('user')) as User;
    if (this.currentUserDetails.image) {
      this.uploadedImage = this.currentUserDetails.image;
    } else if (this.userData && this.userData.data && this.userData.data.image) {
      this.uploadedImage = this.userData.data.image;
    } else {
      this.uploadedImage = null;
    }
  }

  private sendMixpanelEvents(isUploadImage: boolean) {
    if (isUploadImage) {
      this.eventTracker.trackUserAction(UserActionType.ACTION, Page.IMAGE, {
        id: this.currentUserDetails.id,
        name: this.currentUserDetails.firstName,
        type: TargetType.PROFILE_IMAGE
      }, {
        action: UserActionType.UPLOAD,
      }, undefined, null, null);
    } else {
      this.eventTracker.trackUserAction(UserActionType.ACTION, Page.PROFILE, {
        id: this.userDetails.value.id,
        name: this.userDetails.value.firstName + ' ' + this.userDetails.value.lastName,
        type: TargetType.USER
      }, {
        action: UserActionType.UPDATE,
      }, undefined, null, null);
    }
  }

  private handleNotificationTitle(isSuccess?: boolean) {
    this.popupTitle = !isSuccess ? this.translate.instant('notification.error') : this.translate.instant('notification.success');
  }

}



// WEBPACK FOOTER //
// ./src/app/main/account-setting/user-settings/user-details/user-details.component.ts